import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  DEFAULT_ON_DEMAND_AVAILABILITY_LOOKBACK_MINUTES,
  DEFAULT_TRANSFER_GRACE_MINUTES,
  IJourneyLeg,
  IJourneyLocation,
  IJourneyTrip,
  IOnDemandAvailabilityRequest,
  IOnDemandAvailabilityResponse,
  IOnDemandConnectionOption,
  IOnDemandLocation,
  IOperatorMapping,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { OnDemandAvailabilityGateway } from './on-demand-availability.gateway';
import { ReconRouteStore } from '../journey-planning/recon/recon-route-store.service';
import { OperatorMappingStore } from '../operator-mapping/operator-mapping-store.service';
import { LoggingService } from '../../logging/logging.service';
import {
  deduplicateConnectionOptions,
  filterConnectionOptionsByPublicTransportConnections,
  getOnDemandPublicTransportConnectionLimits,
} from '@web/shared-functions/on-demand';

@Injectable({ providedIn: 'root' })
export class OnDemandAvailabilityStore {
  private readonly gateway: OnDemandAvailabilityGateway =
    new OnDemandAvailabilityGateway();
  private readonly reconRouteStore: ReconRouteStore = inject(ReconRouteStore);
  private readonly operatorMappingStore: OperatorMappingStore =
    inject(OperatorMappingStore);

  constructor(private readonly log: LoggingService) {}
  private readonly availabilityTrigger: WritableSignal<
    IOnDemandAvailabilityRequest | undefined
  > = signal(undefined);
  private readonly availabilityResource: ResourceRef<
    IApiResponse<IOnDemandAvailabilityResponse> | undefined
  > = resource({
    params: this.availabilityTrigger,
    loader: ({ params }) => {
      return params
        ? firstValueFrom(this.gateway.checkAvailability(params))
        : Promise.resolve(undefined);
    },
  });

  readonly isLoading: Signal<boolean> = computed(() =>
    this.availabilityResource.isLoading()
  );

  readonly availability: Signal<IOnDemandAvailabilityResponse | undefined> =
    computed(() => this.availabilityResource.value()?.data ?? undefined);

  readonly hasResult: Signal<boolean> = computed(
    () => this.availability() !== undefined
  );

  readonly connectionOptions: Signal<IOnDemandConnectionOption[]> = computed(
    () =>
      deduplicateConnectionOptions(
        filterConnectionOptionsByPublicTransportConnections(
          this.availability()?.connectionOptions ?? [],
          this.reconRouteStore.reconRoute() ?? undefined,
          this.onDemandLeg()
        )
      )
  );

  readonly isAvailable: Signal<boolean> = computed(
    () =>
      (this.availability()?.isAvailable ?? false) &&
      this.connectionOptions().length > 0
  );

  readonly rejectionReason: Signal<string | undefined> = computed(
    () => this.availability()?.rejectionReason
  );

  readonly onDemandLeg: Signal<IJourneyLeg | undefined> = computed(() => {
    const trip: IJourneyTrip = this.reconRouteStore.reconRoute();
    const mappings: IOperatorMapping[] =
      this.operatorMappingStore.operatorMappings();
    if (!trip || !mappings.length) return undefined;

    const operatorIds = mappings.map(m => m.operatorId);
    return trip.legs.find(leg => operatorIds.includes(leg.operator.id));
  });

  readonly matchedMapping: Signal<IOperatorMapping | undefined> = computed(
    () => {
      const leg = this.onDemandLeg();
      if (!leg) return undefined;
      return this.operatorMappingStore
        .operatorMappings()
        .find(m => m.operatorId === leg.operator.id);
    }
  );

  triggerAvailabilityRequest(
    passengerCount: number,
    transferGraceMinutes: number = DEFAULT_TRANSFER_GRACE_MINUTES
  ): void {
    const leg = this.onDemandLeg();
    const mapping = this.matchedMapping();

    if (!leg || !mapping?.id) {
      console.warn(
        'OnDemandAvailabilityStore: No on-demand leg or mapping found.'
      );
      return;
    }

    const trip = this.reconRouteStore.reconRoute() ?? undefined;
    const { earliestOnDemandDeparture, latestOnDemandArrival } =
      getOnDemandPublicTransportConnectionLimits(trip, leg);
    const isOnDemandToPublicTransport =
      earliestOnDemandDeparture === undefined &&
      Number.isFinite(latestOnDemandArrival);
    const departureLookbackMinutes = isOnDemandToPublicTransport
      ? DEFAULT_ON_DEMAND_AVAILABILITY_LOOKBACK_MINUTES
      : transferGraceMinutes;
    const earliestDeparture =
      leg.origin?.time && leg.origin?.date
        ? this.subtractMinutes(
            this.toIsoTimestamp(leg.origin.date, leg.origin.time),
            departureLookbackMinutes
          )
        : undefined;

    const latestArrival = this.getLatestArrival(
      leg,
      latestOnDemandArrival,
      transferGraceMinutes
    );

    this.availabilityTrigger.set({
      operatorMappingId: mapping.id,
      pickUp: this.toOnDemandLocation(leg.origin),
      setDown: this.toOnDemandLocation(leg.destination),
      earliestDeparture,
      latestArrival,
      passengerCount,
    });
  }

  public async validateSelection(
    passengerCount: number,
    extrasPerPassenger: string[][],
    selectedDeparture: string | undefined,
    selectedArrival: string | undefined
  ): Promise<{ isValid: boolean; error?: string }> {
    const leg = this.onDemandLeg();
    const mapping = this.matchedMapping();

    if (!leg || !mapping?.id || !selectedDeparture || !selectedArrival)
      return {
        isValid: false,
        error: 'Keine Bedarfsverkehr-Route gefunden.',
      };

    const params: IOnDemandAvailabilityRequest = {
      operatorMappingId: mapping.id,
      pickUp: this.toOnDemandLocation(leg.origin),
      setDown: this.toOnDemandLocation(leg.destination),
      earliestDeparture: selectedDeparture,
      latestArrival: selectedArrival,
      passengerCount,
      extrasPerPassenger: extrasPerPassenger,
    };

    try {
      const response = await firstValueFrom(
        this.gateway.checkAvailability(params)
      );
      const data = response?.data;

      if (!data) {
        return {
          isValid: false,
          error: 'Keine gültige Antwort vom Server erhalten.',
        };
      }

      if (
        !data.isAvailable ||
        !data.connectionOptions ||
        data.connectionOptions.length === 0
      ) {
        return {
          isValid: false,
          error:
            data.rejectionReason ||
            'Die Fahrt ist mit den gewählten Zusätzen leider nicht mehr verfügbar.',
        };
      }

      const stillAvailable =
        filterConnectionOptionsByPublicTransportConnections(
          data.connectionOptions,
          this.reconRouteStore.reconRoute() ?? undefined,
          leg
        ).find(
          opt =>
            opt.departureTimetabled === selectedDeparture &&
            opt.arrivalTimetabled === selectedArrival
        );

      if (!stillAvailable) {
        return {
          isValid: false,
          error:
            'Die gewählte Abfahrtszeit hat sich durch die Zusätze geändert oder ist bereits ausgebucht.',
        };
      }

      return { isValid: true };
    } catch (err) {
      this.log.error('Validation failed', err);
      return {
        isValid: false,
        error:
          'Fehler bei der Server-Kommunikation. Bitte versuche es noch einmal.',
      };
    }
  }

  private subtractMinutes(isoTime: string, minutes: number): string {
    const date = new Date(isoTime);
    date.setMinutes(date.getMinutes() - minutes);
    return date.toISOString();
  }

  private addMinutes(isoTime: string, minutes: number): string {
    const date = new Date(isoTime);
    date.setMinutes(date.getMinutes() + minutes);
    return date.toISOString();
  }

  private getLatestArrival(
    leg: IJourneyLeg,
    latestOnDemandArrival: number | undefined,
    transferGraceMinutes: number
  ): string | undefined {
    if (
      latestOnDemandArrival !== undefined &&
      Number.isFinite(latestOnDemandArrival)
    ) {
      return new Date(latestOnDemandArrival).toISOString();
    }

    if (leg.destination?.time && leg.destination?.date) {
      return this.addMinutes(
        this.toIsoTimestamp(leg.destination.date, leg.destination.time),
        transferGraceMinutes
      );
    }

    return undefined;
  }

  private toIsoTimestamp(date: string, time: string): string {
    return new Date(`${date}T${time}`).toISOString();
  }

  private toOnDemandLocation(location: IJourneyLocation): IOnDemandLocation {
    return {
      name: location.name,
      latitude: location.latitudeCoord,
      longitude: location.longitudeCoord,
      stopPointRef: location.locationReferenceId || undefined,
    };
  }

  public isOnDemandLeg(leg: IJourneyLeg): boolean {
    const onDemandLeg = this.onDemandLeg();
    return !!onDemandLeg && onDemandLeg.operator.id === leg.operator.id;
  }
}
